import { useAtomValue } from 'jotai';

import { trimFormValues } from '../DataProvider/DataProvider.utils';
import SOURCE_TYPE from '../SourceCreate.constants';
import { buildMultiProviderMessage } from '../SourceCreate.service';
import { acronisTopicsValidatorAtom } from '../SourceCreate.store';
import {
  type ConnectionInputSchema,
  type DynamicFieldData,
  type SourceCreateBaseDTO,
  type SourceCreateDynamicFormModel,
  SourceDialogMode
} from '../SourceCreate.types';
import { mapFormDataToSourceCreateBase } from '../SourceCreate.utils';
import type {
  UseSourceSubmissionParams,
  UseSourceSubmissionReturn
} from './SourceCreateDialog.types';
import {
  buildSourceName,
  categorizeAlertFields
} from './SourceCreateDialog.utils';

const getSelectedTopics = (
  formData: SourceCreateDynamicFormModel
): string[] => {
  const topics = (formData as { topics?: unknown }).topics;

  if (!Array.isArray(topics)) {
    return [];
  }

  return topics.filter(
    (topic): topic is string => typeof topic === 'string' && topic.length > 0
  );
};

/**
 * Custom hook to manage source creation/editing submission logic.
 *
 * Handles:
 * - Form validation
 * - AWS S3 prefix validation with confirmation
 * - Cisco queue name change validation
 * - Form data enrichment (source name, type, categories, regions)
 * - Change tracking for Edit mode (newly added/removed categories, regions, providers)
 * - Medigate alert field categorization
 * - API submission
 *
 * @param params - Dependencies for submission handling
 * @returns Submission handlers
 */
export const useSourceSubmission = ({
  mode,
  isSubmitting,
  setIsSubmitting,
  mutation,
  showSourcePopup,
  getValues,
  trigger,
  setError,
  sourceStatus,
  selectedSourceDetails,
  sourceResponse,
  sourceTypes,
  selectedOrgLabel,
  selectedSubSourceType,
  categories,
  regions,
  cloudProviders,
  authenticationMode,
  newCertificate,
  regenerateCertificate,
  connectionInputSchema,
  setDialogConfig,
  confirmSave,
  getNewlyAddedCategories,
  getNewlyAddedRegions,
  getNewlyAddedCloudProviders,
  getRemovedCategories,
  getRemovedRegions
}: UseSourceSubmissionParams): UseSourceSubmissionReturn => {
  /**
   * Validates AWS S3 data providers and prompts for confirmation if needed.
   *
   * Business rule: When creating AWS S3 sources without a prefix, users must
   * confirm they want to use the default directory structure to prevent
   * accidental broad ingestion of S3 data.
   */
  const validateAwsS3DataProviders = async (
    formData: SourceCreateDynamicFormModel
  ): Promise<boolean> => {
    if (Number(formData.source_type_id) !== SOURCE_TYPE.AWS_S3) {
      return true;
    }

    const selectedProviders: string[] = [];

    if (
      formData?.data_provider?.CiscoUmbrella?.selected &&
      !formData?.data_provider?.CiscoUmbrella?.prefix
    ) {
      selectedProviders.push('cisco');
    }

    if (
      formData?.data_provider?.AWSGuardDuty?.selected &&
      !formData?.data_provider?.AWSGuardDuty?.prefix
    ) {
      selectedProviders.push('guardduty');
    }

    if (
      formData?.data_provider?.DNS_Filter?.selected &&
      !formData?.data_provider?.DNS_Filter?.prefix
    ) {
      selectedProviders.push('dnsfilter');
    }

    if (
      formData?.data_provider?.AWS_CloudTrail?.selected &&
      !formData?.data_provider?.AWS_CloudTrail?.prefix
    ) {
      selectedProviders.push('awscloudtrail');
    }

    if (selectedProviders.length === 0) {
      return true;
    }

    // Show confirmation dialog
    const message = buildMultiProviderMessage(selectedProviders);
    setDialogConfig({
      title: 'Action Required',
      message,
      button: 'Yes'
    });

    const confirmed = await confirmSave();
    return confirmed as boolean;
  };

  /**
   * Validates Cisco Secure Endpoint queue name changes and prompts for confirmation.
   *
   * Business rule: Changing the queue name for Cisco Secure Endpoint sources
   * requires creating a new queue, which needs explicit user confirmation.
   */
  const validateCiscoQueueNameChange = async (
    formData: SourceCreateDynamicFormModel
  ): Promise<boolean> => {
    if (
      mode !== SourceDialogMode.Edit ||
      Number(formData.source_type_id) !== SOURCE_TYPE.CISCO_SECURE_ENDPOINT
    ) {
      return true;
    }

    const srcInputRow = selectedSourceDetails?.source_inputs?.find(
      (item: DynamicFieldData) => item.name === 'name'
    );

    if (!srcInputRow) {
      return true;
    }

    const isQueueNameChanged =
      formData?.sub_source_type === 'Yes' &&
      formData.name !== srcInputRow.value;

    if (isQueueNameChanged) {
      formData.is_queue_name_changed = true;
      setDialogConfig({
        title: 'Action Required',
        message: 'A new queue will be created. Do you want to continue?',
        button: 'Yes'
      });
      const confirmed = await confirmSave();
      return confirmed as boolean;
    }

    formData.is_queue_name_changed = false;
    return true;
  };

  const acronisTopicsValidator = useAtomValue(acronisTopicsValidatorAtom);

  /**
   * Main form submission handler.
   *
   * Flow:
   * 1. Prevent duplicate submissions
   * 2. Trim form values
   * 3. Bootstrap Acronis topics when needed
   * 4. Validate form
   * 5. Validate source-specific business rules (AWS S3 prefixes, Cisco queue names)
   * 6. Enrich form data with metadata (source name, type, categories)
   * 7. Map form data to API payload
   * 8. Submit to backend
   */
  const handleCreateSourceSubmit = async (
    _data: SourceCreateDynamicFormModel
  ) => {
    // Guard: Prevent multiple submissions
    if (isSubmitting || mutation.isPending || !showSourcePopup) {
      return;
    }

    setIsSubmitting(true);

    // Merge the latest form snapshot so conditionally rendered fields such as
    // site_name are preserved even when the submit payload is partial.
    const latestFormValues = getValues();

    // Trim all string values in the form data
    let formData: SourceCreateDynamicFormModel = {
      ..._data,
      ...latestFormValues,
      ...trimFormValues({ ..._data, ...latestFormValues })
    };

    if (
      Number(formData.source_type_id) === SOURCE_TYPE.ACRONIS &&
      categories.includes('EventManager') &&
      acronisTopicsValidator
    ) {
      const hadSelectedTopicsBeforeValidation =
        getSelectedTopics(formData).length > 0;
      const acronisTopicsValid = await acronisTopicsValidator();
      if (!acronisTopicsValid) {
        setIsSubmitting(false);
        return;
      }

      const latestFormValues = getValues();
      formData = {
        ...latestFormValues,
        ...trimFormValues(latestFormValues)
      };

      const hasSelectedTopicsAfterValidation =
        getSelectedTopics(formData).length > 0;

      if (
        !hadSelectedTopicsBeforeValidation &&
        !hasSelectedTopicsAfterValidation
      ) {
        setIsSubmitting(false);
        return;
      }
    }

    // Validate form fields
    const valid = await trigger();
    if (!valid) {
      setIsSubmitting(false);
      return;
    }

    // Validate AWS S3 data provider configuration
    const awsS3Valid = await validateAwsS3DataProviders(formData);
    if (!awsS3Valid) {
      setIsSubmitting(false);
      return;
    }

    setError('');

    // Set source status (active/inactive)
    formData.active = sourceStatus;
    if (mode === SourceDialogMode.Edit && selectedSourceDetails?.edit_status) {
      formData.edit_status = selectedSourceDetails?.edit_status;
    }

    // Validate Cisco Secure Endpoint queue name change
    const ciscoValid = await validateCiscoQueueNameChange(formData);
    if (!ciscoValid) {
      setIsSubmitting(false);
      return;
    }

    // Enrich form data with source type name
    sourceTypes.forEach(
      (sourceType: { id: number; name: string; type?: string }) => {
        if (sourceType.id === Number(formData.source_type_id)) {
          formData.source_type_name = sourceType.name;
        }
      }
    );

    const isFollowUpAuthMode =
      mode === SourceDialogMode.AddThumbPrintId ||
      mode === SourceDialogMode.AddAccessCode;

    if (isFollowUpAuthMode) {
      formData.source_uid =
        formData.source_uid ||
        latestFormValues.source_uid ||
        sourceResponse.source_uid ||
        '';
      formData.source_name =
        formData.source_name ||
        latestFormValues.source_name ||
        sourceResponse.source_name ||
        '';
    } else {
      // Build standardized source name using utility function
      formData.source_name = buildSourceName(
        selectedOrgLabel,
        formData.source_type_name,
        Number(formData.source_type_id),
        formData.site_name,
        selectedSubSourceType || ''
      );
    }

    // Track category/region/provider changes for Edit mode
    // Business rule: Some sources need to know what was added/removed to trigger backend workflows

    // For AWS CloudTrail, Cisco Duo, Proofpoint TAP, Proofpoint POD, JuniperMist and CrowdStrike:
    // newly added categories trigger additional ingestion setup
    const newly_added_categories = getNewlyAddedCategories(
      selectedSourceDetails?.category || [],
      categories,
      selectedSourceDetails?.source_type_id || 0
    );

    if (newly_added_categories.length > 0) {
      formData.newly_added_category = newly_added_categories;
    }

    // For AWS CloudTrail: newly added regions trigger additional ingestion setup
    const newly_added_regions = getNewlyAddedRegions(
      selectedSourceDetails?.region || [],
      regions,
      selectedSourceDetails?.source_type_id || 0
    );
    if (newly_added_regions.length > 0) {
      formData.newly_added_category = newly_added_regions;
    }

    // For CrowdStrike: newly added cloud providers trigger additional ingestion setup
    const newly_added_cloud_providers = getNewlyAddedCloudProviders(
      selectedSourceDetails?.cloud_provider || [],
      cloudProviders,
      selectedSourceDetails?.source_type_id || 0
    );
    if (newly_added_cloud_providers.length > 0) {
      formData.newly_added_cloud_provider = newly_added_cloud_providers;
    }

    // Removed categories or regions handling is done in the backend based on
    // the current selected vs existing categories/regions to clean up ingestion
    if (selectedSourceDetails?.show_categories) {
      const removed_categories = getRemovedCategories(
        selectedSourceDetails?.category || [],
        categories
      );

      if (removed_categories.length > 0) {
        formData.removed_categories = removed_categories;
      }
    }

    if (selectedSourceDetails?.show_regions) {
      const removed_regions = getRemovedRegions(
        selectedSourceDetails?.region || [],
        regions
      );
      if (removed_regions.length > 0) {
        formData.removed_categories = removed_regions;
      }
    }

    if (mode === SourceDialogMode.AddThumbPrintId) {
      const thumbprintRequest = {
        thumbprint_id:
          formData.thumbprint_id || latestFormValues.thumbprint_id || '',
        source_uid:
          formData.source_uid ||
          latestFormValues.source_uid ||
          sourceResponse.source_uid ||
          '',
        source_name:
          formData.source_name ||
          latestFormValues.source_name ||
          sourceResponse.source_name ||
          ''
      };

      try {
        await mutation.mutateAsync(thumbprintRequest as SourceCreateBaseDTO);
      } catch (error) {
        console.error('Error saving source thumbprint:', error);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (mode === SourceDialogMode.AddAccessCode) {
      const accessCodeRequest = {
        access_code: formData.access_code || latestFormValues.access_code || '',
        source_uid:
          formData.source_uid ||
          latestFormValues.source_uid ||
          sourceResponse.source_uid ||
          '',
        source_name:
          formData.source_name ||
          latestFormValues.source_name ||
          sourceResponse.source_name ||
          ''
      };

      await mutation.mutateAsync(accessCodeRequest as SourceCreateBaseDTO);
      setIsSubmitting(false);
      return;
    }

    // Medigate-specific: Categorize alert device fields by their category
    // Business rule: Medigate alert fields are submitted as nested objects grouped by category
    if (
      Number(formData?.source_type_id) === SOURCE_TYPE.MEDIGATE &&
      formData?.device_alert_fields
    ) {
      const categorizedKeys = categorizeAlertFields(
        formData.device_alert_fields
      );
      formData = { ...formData, ...categorizedKeys };
    }

    try {
      const requestData = await mapFormDataToSourceCreateBase(
        formData,
        mode,
        authenticationMode,
        newCertificate,
        regenerateCertificate,
        connectionInputSchema as ConnectionInputSchema,
        selectedOrgLabel
      );
      await mutation.mutateAsync(requestData as SourceCreateBaseDTO);
    } catch (error) {
      console.error('Error mapping form data:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleError = () => {
    trigger(); // Force validation to show all errors
  };

  return {
    handleCreateSourceSubmit,
    handleError
  };
};
